import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_PRODUCT_GROUPS_QUERY,
    getProductGroupQueryVariables,
} from '../queries/getProductGroupsQuery';
import { ProductGroup } from '../types/ProductGroup';
import { transformProductGroupResponse } from '../tools/transformProductGroupResponse';
import { GetProductGroupListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type ProductGroupById = Record<string, ProductGroup>;
type ProductGroupByKey = Record<string, ProductGroup>;
type ProductGroupByDmDefinitionId = Record<string, ProductGroup>;

interface ProductGroupContextValue {
    productGroups: ProductGroup[];
    productGroupById: ProductGroupById;
    productGroupByKey: ProductGroupByKey;
    productGroupByDmDefinitionId: ProductGroupByDmDefinitionId;
    loading: boolean;
    error: any;
    initialize: () => void;
}

const ProductGroupContext = createContext<ProductGroupContextValue | undefined>(undefined);

interface ProductGroupProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ProductGroupsProvider = ({ children, apolloClient }: ProductGroupProviderProps) => {
    const { locale } = useLocale();

    const [productGroupById, setProductGroupById] = useState<ProductGroupById>({});
    const [productGroupByKey, setProductGroupByKey] = useState<ProductGroupByKey>({});
    const [productGroupByDmDefinitionId, setProductGroupByDmDefinitionId] =
        useState<ProductGroupByDmDefinitionId>({});

    const [initialized, setInitialized] = useState(false);

    const query = GET_PRODUCT_GROUPS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetProductGroupListingQuery>(query, {
        variables: getProductGroupQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError });

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    const productGroups = useMemo(() => {
        const data = fallbackData ?? queryData;
        if (!data) {
            return [];
        }

        const productGroups = transformProductGroupResponse(data);

        const sortTreeByName = (groups: ProductGroup[]): ProductGroup[] => {
            return groups
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, locale))
                .map((group) => ({
                    ...group,
                    children: group.children ? sortTreeByName(group.children) : undefined,
                }));
        };

        return sortTreeByName(productGroups);
    }, [queryData, fallbackData, locale]);

    useEffect(() => {
        const productGroupById: ProductGroupById = {};
        const productGroupByKey: ProductGroupByKey = {};
        const productGroupByDmDefinitionId: ProductGroupByDmDefinitionId = {};

        productGroups.forEach((group) => {
            productGroupById[group.id] = group;
            productGroupByKey[group.key] = group;
            if (group.dmDefinitionId) {
                const existingGroup = productGroupByDmDefinitionId[group.dmDefinitionId];
                if (existingGroup && existingGroup.id !== group.id) {
                    console.error(
                        `Duplicate dmDefinitionId found: ${group.dmDefinitionId} for group "${group.key}" (${group.id}) and "${existingGroup?.key}" (${existingGroup.id}) in ProductGroupsProvider. This may lead to unexpected behavior.`
                    );
                }
                productGroupByDmDefinitionId[group.dmDefinitionId] = group;
            }
            group.children?.forEach((child) => {
                productGroupById[child.id] = child;
                productGroupByKey[`${group.key}/${child.key}`] = child;
            });
        });

        setProductGroupById(productGroupById);
        setProductGroupByKey(productGroupByKey);
        setProductGroupByDmDefinitionId(productGroupByDmDefinitionId);
    }, [productGroups]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ProductGroupContext.Provider
            value={{
                productGroups,
                productGroupById,
                productGroupByKey,
                productGroupByDmDefinitionId,
                loading,
                error,
                initialize,
            }}
        >
            {children}
        </ProductGroupContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useProductGroupsUnsafe() {
    const context = useContext(ProductGroupContext);
    if (!context) {
        throw new Error('useProductGroupsUnsafe must be used within a ProductGroupsProvider');
    }
    return context;
}

// Safe hook: triggers lazy initialization when a consumer uses it.
function useProductGroups() {
    const context = useProductGroupsUnsafe();
    // Trigger lazy initialization when a consumer uses this hook.
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export { ProductGroupsProvider, useProductGroups, useProductGroupsUnsafe };
export type {
    ProductGroupContextValue,
    ProductGroupById,
    ProductGroupByKey,
    ProductGroupByDmDefinitionId,
};
